import { Button, Flex, Select, Divider, Spin, Tour, Tooltip } from 'antd'
import { useState, useEffect, useRef, useMemo } from 'react'
import { CloseOutlined } from '@ant-design/icons'
import { useDivisions } from '../../../../domain/divisions/divisionsQueries'
import { useSalesPeopleQuery, useUpdateJobPropertyGeolocationMutationNoInvalidate, useDeactivateJobMutation, useRoutingDataQuery, useAllRouteJobsQuery } from '../../routingQueries'
import { getJobUrl } from '../../../../domain/jobs/jobsApi'
import { getGeocoding } from '../../Mappers/mapbox'
import { useQueryClient } from 'react-query'
import { projectTypeOptionsFor } from '../../../../common/constants'
import useServiceTicketsEnabled from '../../../../hooks/useServiceTicketsEnabled'
import { faWarning } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as routeAddModel from '../routeAddModel'
import Map, { NavigationControl, MapRef, FullscreenControl } from 'react-map-gl/mapbox'
import 'mapbox-gl/dist/mapbox-gl.css'
import ManageShopAddressModal from '../ManageShopAddressModal'
import { JobAddressModal } from '../JobAddressModal'
import { AddJobsRecord } from '../../routingApi'
import { TenantLocationType } from '../../../../domain/tenantLocations/tenantLocation'
import ShopMarker from '../../RouteMap/ShopMarker'
import ShopMarkerSmall from '../../RouteMap/ShopMarkerSmall'
import JobsListSection from './JobsListSection'
import AddJobsMapMarker from './AddJobsMapMarker'
import AddJobsByMapLegend from './AddJobsByMapLegend'
import ClusterMarker from './ClusterMarker'
import ClusterPopup from './ClusterPopup'
import { useJobClustering, JobCluster } from './useJobClustering'
import { useTenantLocationsDataQuery } from '../../../../domain/tenantLocations/tenantLocationsQueries'
import { faQuestionCircle } from '@fortawesome/pro-duotone-svg-icons'

export type Props = {
  onClose: () => void
  onNext: (selectedJobs: number[], jobsData: routeAddModel.Job[]) => void
  initialSelectedJobs?: number[]
  jobs: AddJobsRecord[] | undefined
  routeJobIds?: number[]
  initialMapViewState: any
  isPreRender?: boolean
  isOpen: boolean
}

const AddJobsByMap = ({ onClose, onNext, initialSelectedJobs = [], jobs, routeJobIds = [], initialMapViewState, isPreRender = false, isOpen }: Props) => {
  const showServiceTickets = useServiceTicketsEnabled()
  const projectTypeOptions = projectTypeOptionsFor(showServiceTickets)
  const [isManageShopAddressModalOpen, setIsManageShopAddressModalOpen] = useState(false)
  const [isJobAddressModalOpen, setIsJobAddressModalOpen] = useState(false)
  const [selectedJobForAddress, setSelectedJobForAddress] = useState<AddJobsRecord | null>(null)
  const [filters, setFilters] = useState<routeAddModel.AddRouteJobsFilters>(routeAddModel.defaultAddRouteJobsFilters)
  const [selectedJobs, setSelectedJobs] = useState<number[]>(initialSelectedJobs)
  const [mapViewState] = useState(initialMapViewState)

  const [activeCluster, setActiveCluster] = useState<JobCluster | null>(null)
  const [shopAddressMissing, setShopAddressMissing] = useState(false)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [currentZoom, setCurrentZoom] = useState(8)
  const [isAutoResolving, setIsAutoResolving] = useState(false)
  const autoResolveAttemptedRef = useRef<Set<number>>(new Set())
  const isAutoResolvingRef = useRef(false)
  const mapRef = useRef<MapRef>(null)
  const viewed = window.localStorage.getItem('add-jobs-by-map-tour-viewed') === 'true'
  const [isTourOpen, setIsTourOpen] = useState(false)

  const { data: divisions } = useDivisions()
  const { data: salesPersons } = useSalesPeopleQuery()
  const { data: tenantLocations, isLoading: isLoadingTenantLocations } = useTenantLocationsDataQuery()
  const { data: routes } = useRoutingDataQuery()
  const { data: allRouteJobs } = useAllRouteJobsQuery()
  const shopLocation = tenantLocations?.find(tl => tl.type === TenantLocationType.Shop)
  const queryClient = useQueryClient()
  const { mutateAsync: updateJobPropertyGeolocationAsync } = useUpdateJobPropertyGeolocationMutationNoInvalidate()
  const { mutate: deactivateJob } = useDeactivateJobMutation()

  const handleViewJob = async (jobId: number) => {
    const url = await getJobUrl(jobId)
    window.open(url, '_blank')
  }

  const handleDeactivateJob = (jobId: number) => {
    deactivateJob(jobId)
  }

  const divisionOptions = useMemo(() => {
    const allDivisions = divisions || []
    const filtered = (!filters.projectTypeIds || filters.projectTypeIds.length === 0)
      ? allDivisions
      : allDivisions.filter(d => filters.projectTypeIds.includes(d.type as unknown as number))

    return filtered
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(d => ({ label: d.name, value: d.id }))
  }, [divisions, filters.projectTypeIds])

  const filteredJobsWithCoordinates = useMemo(() => {
    return routeAddModel.getFilteredJobs(jobs || [], filters, selectedJobs, routeJobIds)
      .filter(job => job.latitude && job.longitude && (job.active || job.type === '1'))
  }, [jobs, filters, selectedJobs, routeJobIds])

  const clusters = useJobClustering(filteredJobsWithCoordinates, currentZoom)

  const toggleJobSelection = (jobId: number) => {
    const job = jobs?.find(j => j.jobId === jobId)

    if (job && (routeAddModel.hasJobError(job) || routeAddModel.hasNoWorkAreas(job) || routeAddModel.hasNoApprovedWorkAreas(job))) {
      return
    }

    setSelectedJobs(prev => {
      const isCurrentlySelected = prev.includes(jobId)

      if (isCurrentlySelected) {
        return prev.filter(id => id !== jobId)
      } else {
        return [...prev, jobId]
      }
    })
  }

  const addJobsToSelection = (jobIds: number[]) => {
    setSelectedJobs(prev => Array.from(new Set([...prev, ...jobIds])))
  }

  const removeJobsFromSelection = (jobIds: number[]) => {
    const toRemove = new Set(jobIds)
    setSelectedJobs(prev => prev.filter(id => !toRemove.has(id)))
  }

  const handleMarkerClick = (coords: string, e: any) => {
    if (isPreRender) return // Don't handle clicks in pre-render mode

    if (!e?.originalEvent) return

    e.originalEvent.stopPropagation()

    const allJobsAtLocation = getJobsAtLocation(coords)

    const allJobsDisabled = allJobsAtLocation.every(job => routeAddModel.hasJobError(job) || routeAddModel.hasNoWorkAreas(job) || routeAddModel.hasNoApprovedWorkAreas(job))
    if (allJobsDisabled) {
      return
    }

    if (allJobsAtLocation.length === 1) {
      toggleJobSelection(allJobsAtLocation[0].jobId)
    } else {
      const firstJob = allJobsAtLocation[0]
      const cluster: JobCluster = {
        id: `marker-${coords}`,
        latitude: firstJob.latitude!,
        longitude: firstJob.longitude!,
        jobs: allJobsAtLocation,
        isCluster: true
      }
      setActiveCluster(cluster)
    }
  }

  const getJobsAtLocation = (coords: string): routeAddModel.Job[] => {
    return filteredJobsWithCoordinates.filter(job => coords === `${job.longitude},${job.latitude}`) || []
  }

  const handleClusterClick = (cluster: JobCluster) => {
    if (isPreRender) return // Don't handle clicks in pre-render mode

    if (cluster.jobs.length === 1) {
      toggleJobSelection(cluster.jobs[0].jobId)
    } else {
      setActiveCluster(cluster)
    }
  }

  const handleAddressSelect = () => {
    if (selectedJobForAddress) {
      setIsJobAddressModalOpen(false)
      setSelectedJobForAddress(null)
    }
  }

  const MAP_ZOOM = 8

  useEffect(() => {
    if (isMapLoaded && !isPreRender) {
      if (!shopLocation && !isLoadingTenantLocations) {
        setIsManageShopAddressModalOpen(true)
        setShopAddressMissing(true)
      }
    }
  }, [shopLocation, isMapLoaded, isPreRender])

  useEffect(() => {
    if (!viewed && !isPreRender && !isManageShopAddressModalOpen && shopLocation) {
      const timer = setTimeout(() => {
        setIsTourOpen(true)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [viewed, isPreRender, isManageShopAddressModalOpen, shopLocation])

  useEffect(() => {
    if (isPreRender || !jobs || isAutoResolvingRef.current) return

    const resolveAutomatically = async () => {
      const jobsToResolve = jobs.filter(job =>
        (job.active || job.type === '1') &&
        (!job.latitude || !job.longitude) &&
        job.address1 && job.city && job.state && job.zip &&
        !autoResolveAttemptedRef.current.has(job.jobId)
      )

      if (jobsToResolve.length === 0) return

      isAutoResolvingRef.current = true
      setIsAutoResolving(true)

      for (const job of jobsToResolve) {
        autoResolveAttemptedRef.current.add(job.jobId)

        const addressString = [
          job.address1,
          job.address2,
          job.city,
          job.state,
          job.zip
        ].filter(Boolean).join(' ')

        if (!addressString.trim() || !job.propertyId) {
          continue
        }

        try {
          const result = await getGeocoding(addressString)
          if (result) {
            const context = result.properties.context || {}
            const address = context.address?.name || result.properties.address_line1 || ''
            const city = context.place?.name || ''
            const state = context.region?.region_code || ''
            const zip = context.postcode?.name || ''
            const county = context.district?.name || ''
            const country = context.country?.country_code || 'US'

            const latitude = result.geometry.coordinates[1]
            const longitude = result.geometry.coordinates[0]

            await updateJobPropertyGeolocationAsync({
              propertyId: job.propertyId,
              address1: address,
              address2: null,
              city: city,
              state: state,
              zip: zip,
              county: county,
              countryCode: country,
              latitude: latitude,
              longitude: longitude,
              nickname: ''
            })
          }
        } catch (error) {
        }
      }

      queryClient.invalidateQueries(['jobsForCreateRoute'])
      isAutoResolvingRef.current = false
      setIsAutoResolving(false)
    }

    resolveAutomatically()
  }, [jobs, isPreRender, queryClient, updateJobPropertyGeolocationAsync])

  const tourSteps = [
    {
      title: 'Filter Jobs',
      description: 'Use these filters to narrow down jobs by division, job type, or sales person. This helps you find the right jobs for your route more efficiently.',
      target: () => document.getElementById('filter-row') as HTMLElement || null,
    },
    {
      title: 'Map Clusters',
      description: 'Jobs on the map are grouped into clusters when they are close together. Click on clusters or individual markers to select jobs for your route.',
      target: () => document.getElementById('map-clusters-section') as HTMLElement || null,
    },
    {
      title: 'Search Jobs',
      description: 'Search for specific jobs, clients, or addresses by typing in the search box. This makes it easy to find particular jobs you need to add to your route.',
      target: () => document.getElementById('search-jobs-input') as HTMLElement || null,
    },
    {
      title: 'Selected Jobs Filter',
      description: 'Toggle this switch to show only the jobs you have already selected, making it easier to review your choices.',
      target: () => document.getElementById('selected-jobs-filter') as HTMLElement || null,
    },
    {
      title: 'Edit Addresses',
      description: 'Click on any job address to edit or correct it. This opens a modal where you can update the address information.',
      target: () => document.getElementById('edit-address-section') as HTMLElement || null,
    },
    {
      title: 'Resolve Address Issues',
      description: 'When a job has address or location issues, use this Resolve button to automatically fix missing coordinates and add the job to the map.',
      target: () => document.getElementById('resolve-address-button') as HTMLElement || null,
    },
    {
      title: 'Next Button',
      description: 'Once you have selected jobs for your route, click Next to configure your jobs for estimated duration and work areas.',
      target: () => document.getElementById('next-button') as HTMLElement || null,
    },
  ]

  return <>
    <Flex vertical style={{ height: '100%', position: 'relative' }}>
      {!isPreRender && (
        <Flex justify="space-between" align="center" style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
          <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Add Jobs
            <Tooltip title="Need help? Take a quick tour of the job selection features">
              <Button
                type="text"
                size="small"
                onClick={() => {
                  setIsTourOpen(true)
                }}
                style={{ padding: '4px' }}
              >
                <FontAwesomeIcon icon={faQuestionCircle} />
              </Button>
            </Tooltip>
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CloseOutlined onClick={onClose} style={{ fontSize: '16px', cursor: 'pointer' }} />
          </div>
        </Flex>
      )}

      {!isPreRender && (
        <Flex id="filter-row" gap="small" style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
          <span style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>Filter by:</span>
          <Select
            style={{ width: 200 }}
            placeholder="Job Type"
            value={filters.projectTypeIds}
            onChange={(value) => setFilters({ ...filters, projectTypeIds: value })}
            options={projectTypeOptions}
            mode="multiple"
            maxTagCount={1}
            optionFilterProp='label'
          />
          <Select
            style={{ width: 300 }}
            placeholder="Division"
            value={filters.divisionIds}
            onChange={(value) => setFilters({ ...filters, divisionIds: value })}
            options={divisionOptions}
            mode="multiple"
            maxTagCount={1}
            optionFilterProp='label'
          />
          <Select
            style={{ width: 300 }}
            placeholder="Sales Person"
            value={filters.salesPersonIds}
            onChange={(value) => setFilters({ ...filters, salesPersonIds: value })}
            options={salesPersons?.map((s) => ({ label: s.displayName, value: s.email })) || []}
            mode="multiple"
            maxTagCount={1}
            optionFilterProp='label'
          />
          {selectedJobs.length > 0 && (
            <div id="selected-jobs-indicator" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
              <Divider type="vertical" style={{ height: '24px', margin: '0 12px' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  backgroundColor: '#FFCABD',
                  color: '#FF5C35',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  border: '1px solid #FF5C35'
                }}>
                  {selectedJobs.length}
                </div>
                <span style={{ fontWeight: 'bold' }}>Jobs Selected</span>
              </div>
            </div>
          )}
        </Flex>
      )}

      <Flex style={{ height: isPreRender ? '100%' : 'calc(100% - 165px)' }}>
        <div style={{
          flex: 1,
          height: '100%',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Map
            ref={mapRef}
            mapboxAccessToken={(window as any).env.MAPBOX_ACCESS_TOKEN}
            initialViewState={shopLocation ? {
              longitude: shopLocation.longitude,
              latitude: shopLocation.latitude,
              zoom: MAP_ZOOM,
              bearing: 0,
              pitch: 0,
              padding: { top: 0, bottom: 0, left: 0, right: 0 }
            } : undefined}
            onLoad={() => {
              setIsMapLoaded(true)
            }}
            onMove={(evt) => {
              setCurrentZoom(evt.viewState.zoom)
            }}
            mapStyle="mapbox://styles/mapbox/light-v11"
            style={{ width: '100%', height: '98%' }}
          >
            {!isMapLoaded && <div style={{ width: '100%', height: '100%', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Spin />
            </div>}
            <NavigationControl position="top-left" />
            <FullscreenControl position="bottom-left" />

            {isAutoResolving && (
              <div style={{
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2,
                backgroundColor: '#e6f7ff',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                padding: '12px 16px',
                border: '1px solid #91d5ff',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <Spin size="small" />
                <div style={{ fontSize: '13px', color: '#0050b3' }}>
                  Resolving addresses...
                </div>
              </div>
            )}

            <div style={{
              position: 'absolute',
              top: '10px',
              left: '50px',
              zIndex: 1,
              backgroundColor: 'white',
              borderRadius: '20px',
              boxShadow: '0 0 10px rgba(0,0,0,0.1)',
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              maxWidth: '400px',
              border: '1px solid #e8e8e8'
            }}
              onClick={() => setIsManageShopAddressModalOpen(true)}>
              {shopLocation && (
                <ShopMarkerSmall />
              )}

              {shopLocation ? (
                <span style={{ color: '#333', fontSize: '12px', whiteSpace: 'nowrap', textDecoration: 'underline' }}>
                  {shopLocation?.address1}{' '}
                  {shopLocation?.city}{' '}
                  {shopLocation?.stateCode}, {' '}
                  {shopLocation?.zip}
                </span>
              ) : (
                <span style={{ color: '#FF5C35', fontStyle: 'italic', fontSize: '12px', display: 'flex', alignItems: 'center', textDecoration: 'underline' }}>
                  <FontAwesomeIcon icon={faWarning} style={{ marginRight: '6px', fontSize: '12px' }} />
                  Shop address is required
                </span>
              )}
            </div>

            {shopLocation?.latitude &&
              shopLocation?.longitude && (
                <ShopMarker
                  latitude={shopLocation.latitude!}
                  longitude={shopLocation.longitude!}
                />
              )}

            <AddJobsByMapLegend />

            {clusters.map(cluster => {
              if (cluster.isCluster) {
                return (
                  <ClusterMarker
                    key={cluster.id}
                    cluster={cluster}
                    selectedJobs={selectedJobs}
                    onClusterClick={handleClusterClick}
                  />
                )
              } else {
                const job = cluster.jobs[0]
                const isSelected = selectedJobs.includes(job.jobId)
                const coords = `${job.longitude},${job.latitude}`

                return (
                  <AddJobsMapMarker
                    key={job.jobId}
                    coords={coords}
                    job={job}
                    isSelected={isSelected}
                    handleMarkerClick={handleMarkerClick}
                    selectedJobs={selectedJobs}
                    jobs={jobs || []}
                    filters={filters}
                    filteredJobs={filteredJobsWithCoordinates}
                  />
                )
              }
            })}
          </Map>
        </div>

        {/* Jobs List Section */}
        {!isPreRender && (
          <JobsListSection
            jobs={jobs?.filter(job => job.active || job.type === '1') || []}
            routeJobIds={routeJobIds}
            filters={filters}
            setFilters={setFilters}
            selectedJobs={selectedJobs}
            toggleJobSelection={toggleJobSelection}
            addJobsToSelection={addJobsToSelection}
            removeJobsFromSelection={removeJobsFromSelection}
            setSelectedJobForAddress={setSelectedJobForAddress}
            setIsJobAddressModalOpen={setIsJobAddressModalOpen}
            onViewJob={handleViewJob}
            onDeactivateJob={handleDeactivateJob}
            routes={routes}
            allRouteJobs={allRouteJobs}
          />
        )}
      </Flex>

      {/* Footer with Navigation */}
      {!isPreRender && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          backgroundColor: 'white',
          zIndex: 10,
          height: '65px'
        }}>
          <Flex gap="small" justify="flex-end" style={{ marginRight: '60px' }}>
            <Button type="default" onClick={onClose}>Cancel</Button>
            <Button
              id="next-button"
              type="primary"
              disabled={selectedJobs.length === 0 || shopAddressMissing}
              onClick={() => onNext(selectedJobs, selectedJobs.map(id => jobs?.find(job => job.jobId === id)).filter(Boolean) as routeAddModel.Job[])}
            >
              Next →
            </Button>
          </Flex>
        </div>
      )}
    </Flex>

    {/* Cluster Popup */}
    {!isPreRender && (
      <ClusterPopup
        cluster={activeCluster}
        selectedJobs={selectedJobs}
        onClose={() => setActiveCluster(null)}
        onToggleJobSelection={toggleJobSelection}
      />
    )}
    {!isPreRender && (
      <>
        <ManageShopAddressModal
          isOpen={isManageShopAddressModalOpen}
          onClose={() => setIsManageShopAddressModalOpen(false)}
          proximity={`${mapViewState.longitude},${mapViewState.latitude}`}
          shopAddress={shopLocation || undefined}
          closeable={!!shopLocation}
          onAddressUpdate={(coordinates) => {
            if (mapRef.current) {
              mapRef.current.flyTo({
                center: [coordinates.longitude, coordinates.latitude],
                zoom: routeAddModel.DEFAULT_MAP_ZOOM,
                duration: 5000
              })
            }
            setShopAddressMissing(false)
          }}
        />
        <JobAddressModal
          isOpen={isJobAddressModalOpen}
          onClose={() => {
            setIsJobAddressModalOpen(false)
            setSelectedJobForAddress(null)
          }}
          onAddressSelect={handleAddressSelect}
          job={selectedJobForAddress}
          proximity={`${mapViewState.longitude},${mapViewState.latitude}`}
          address={selectedJobForAddress ?
            [
              selectedJobForAddress.address1,
              selectedJobForAddress.address2,
              selectedJobForAddress.city,
              selectedJobForAddress.state,
              selectedJobForAddress.zip
            ].filter(Boolean).join(', ')
            : undefined}
        />

        <Tour
          open={isTourOpen && isOpen}
          onClose={() => {
            setIsTourOpen(false)
          }}
          onFinish={() => {
            setIsTourOpen(false)
            window.localStorage.setItem('add-jobs-by-map-tour-viewed', 'true')
          }}
          steps={tourSteps.filter(step => step.target() !== null)}
          type="default"
          disabledInteraction={true}
          closable={false}
        />
      </>
    )}
  </>
}

export default AddJobsByMap